import { useCallback, useEffect, useState } from 'react';
import { adminApi as defaultAdminApi } from './admin-api';
import { useAuth } from '../auth/AuthContext';
import { errorMessageFor } from '../auth/error-message';
import './admin.css';

const DAYS = [
  ['monday', 'Lunes'], ['tuesday', 'Martes'], ['wednesday', 'Miércoles'],
  ['thursday', 'Jueves'], ['friday', 'Viernes'], ['saturday', 'Sábado'], ['sunday', 'Domingo'],
];

function emptySettings() {
  return {
    gymName: '', address: '', phone: '', socialLinks: [], openingHoursText: '',
    studentSchedule: DAYS.map(([day]) => ({ day, visible: false, startTime: null, endTime: null })),
    welcomeMessage: '',
  };
}

function normalizeSettings(settings) {
  const empty = emptySettings();
  return {
    ...empty,
    ...settings,
    socialLinks: (settings.socialLinks || []).map(link => ({ label: link.label, url: link.url })),
    studentSchedule: DAYS.map(([day]) => {
      const row = (settings.studentSchedule || []).find(item => item.day === day);
      return row ? { day, visible: Boolean(row.visible), startTime: row.startTime || null, endTime: row.endTime || null }
        : empty.studentSchedule.find(item => item.day === day);
    }),
  };
}

export function SettingsPage({ api = defaultAdminApi }) {
  const { handleApiError } = useAuth();
  const [loadState, setLoadState] = useState({ status: 'loading', message: '' });
  const [settings, setSettings] = useState(emptySettings);
  const [saveState, setSaveState] = useState('idle');
  const [error, setError] = useState('');

  const load = useCallback(() => {
    let active = true;
    setLoadState({ status: 'loading', message: '' });
    api.getSettings().then(({ settings: dto }) => {
      if (active) {
        setSettings(normalizeSettings(dto));
        setLoadState({ status: 'ready', message: '' });
      }
    }).catch(problem => {
      if (!active) return;
      const outcome = handleApiError(problem);
      if (!outcome.handled) setLoadState({ status: 'error', message: errorMessageFor(problem) });
    });
    return () => { active = false; };
  }, [api, handleApiError]);

  useEffect(() => load(), [load]);

  function change(key, value) {
    setSettings(current => ({ ...current, [key]: value }));
    setSaveState('idle');
    setError('');
  }

  function changeDay(day, key, value) {
    change('studentSchedule', settings.studentSchedule.map(row => row.day === day
      ? { ...row, [key]: value, ...(key === 'visible' && !value ? { startTime: null, endTime: null } : {}) }
      : row));
  }

  async function save(event) {
    event.preventDefault();
    if (saveState === 'saving') return;
    setSaveState('saving');
    setError('');
    try {
      const { settings: saved } = await api.updateSettings(settings);
      setSettings(normalizeSettings(saved));
      setSaveState('saved');
    } catch (problem) {
      const outcome = handleApiError(problem);
      if (!outcome.handled) {
        setError(errorMessageFor(problem));
        setSaveState('idle');
      }
    }
  }

  function changeSocial(index, key, value) {
    change('socialLinks', settings.socialLinks.map((link, i) => i === index ? { ...link, [key]: value } : link));
  }

  return (
    <section className="gym-page" aria-labelledby="gym-page-title">
      <header className="gym-page__header">
        <h1 id="gym-page-title" className="gym-page__title">Configuración</h1>
      </header>
      {loadState.status === 'loading' && <div role="status" className="gym-state gym-state--loading"><p className="gym-state__text">Cargando…</p></div>}
      {loadState.status === 'error' && <div role="alert" className="gym-state gym-state--error"><p className="gym-state__text">{loadState.message}</p><button type="button" className="gym-button gym-button--secondary" onClick={load}>Reintentar</button></div>}
      {loadState.status === 'ready' && <form className="gym-admin-settings" onSubmit={save}>
        <fieldset className="gym-card gym-admin-settings__section">
          <legend className="gym-admin-settings__legend">Datos del gimnasio</legend>
          <div className="gym-admin-settings__identity">
            <div className="gym-admin-settings__logo" aria-label="Logo HAMMER fijo"><img src={`${process.env.PUBLIC_URL || ''}/brand/hammer-mark.png`} alt="" /></div>
            <span className="gym-admin-settings__logo-caption">Logo HAMMER</span>
          </div>
          <label className="gym-field"><span>Nombre del gimnasio</span><input className="gym-input" value={settings.gymName} onChange={e => change('gymName', e.target.value)} maxLength="100" required /></label>
          <label className="gym-field"><span>Dirección</span><input className="gym-input" value={settings.address} onChange={e => change('address', e.target.value)} maxLength="240" /></label>
          <label className="gym-field"><span>Teléfono</span><input className="gym-input" type="tel" value={settings.phone} onChange={e => change('phone', e.target.value)} maxLength="40" /></label>
        </fieldset>

        <fieldset className="gym-card gym-admin-settings__section">
          <legend className="gym-admin-settings__legend">Redes sociales</legend>
          <p className="gym-admin-settings__hint">Agregá hasta ocho enlaces públicos con dirección web completa.</p>
          {settings.socialLinks.map((link, index) => <div className="gym-card gym-admin-settings__social" key={`social-${index}`}>
            <label className="gym-field"><span>Red social {index + 1}</span><input className="gym-input" value={link.label} maxLength="30" onChange={e => changeSocial(index, 'label', e.target.value)} /></label>
            <label className="gym-field"><span>URL {index + 1}</span><input className="gym-input" type="url" value={link.url} onChange={e => changeSocial(index, 'url', e.target.value)} /></label>
            <button type="button" className="gym-button gym-button--secondary" aria-label={`Quitar red social ${index + 1}`} onClick={() => change('socialLinks', settings.socialLinks.filter((_, i) => i !== index))}>Quitar</button>
          </div>)}
          {settings.socialLinks.length < 8 && <button type="button" className="gym-button gym-button--secondary" onClick={() => change('socialLinks', [...settings.socialLinks, { label: '', url: '' }])}>Agregar red social</button>}
        </fieldset>

        <fieldset className="gym-card gym-admin-settings__section">
          <legend className="gym-admin-settings__legend">Horarios</legend>
          <label className="gym-field"><span>Horario para recepción</span><textarea className="gym-input gym-admin-settings__textarea" value={settings.openingHoursText} maxLength="1000" onChange={e => change('openingHoursText', e.target.value)} /></label>
          <p className="gym-admin-settings__hint">Elegí qué días y horarios se mostrarán a los alumnos.</p>
          <div className="gym-admin-settings__days">
            {DAYS.map(([day, label]) => {
              const row = settings.studentSchedule.find(item => item.day === day);
              return <div className="gym-admin-settings__day" key={day}>
                <label className="gym-admin-settings__day-toggle"><input type="checkbox" aria-label={`${label} abierto`} checked={row.visible} onChange={e => changeDay(day, 'visible', e.target.checked)} /><span>{label}</span></label>
                {row.visible && <>
                  <label className="gym-field"><span>Inicio {label.toLowerCase()}</span><input className="gym-input" type="time" aria-label={`Inicio ${label.toLowerCase()}`} value={row.startTime || ''} onChange={e => changeDay(day, 'startTime', e.target.value || null)} /></label>
                  <label className="gym-field"><span>Fin {label.toLowerCase()}</span><input className="gym-input" type="time" aria-label={`Fin ${label.toLowerCase()}`} value={row.endTime || ''} onChange={e => changeDay(day, 'endTime', e.target.value || null)} /></label>
                </>}
              </div>;
            })}
          </div>
        </fieldset>

        <fieldset className="gym-card gym-admin-settings__section">
          <legend className="gym-admin-settings__legend">Inicio del alumno</legend>
          <label className="gym-field"><span>Mensaje de bienvenida</span><textarea className="gym-input gym-admin-settings__textarea" value={settings.welcomeMessage} maxLength="1000" onChange={e => change('welcomeMessage', e.target.value)} /></label>
        </fieldset>

        {error && <p role="alert" className="gym-form__error">{error}</p>}
        <div className="gym-admin-form-actions gym-admin-settings__save-actions">
          <button type="submit" className="gym-button gym-button--primary" disabled={saveState === 'saving'}>{saveState === 'saving' ? 'Guardando…' : 'Guardar cambios'}</button>
          {saveState === 'saved' && <p role="status" className="gym-admin-settings__saved">Cambios guardados.</p>}
        </div>
      </form>}
    </section>
  );
}
